import axios, { CancelTokenSource } from 'axios';
import Input, { StyledInput } from 'components/final-form/Input';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import SearchByButtonsStyle from 'components/layout/SearchByButtonsStyle';
import LoaderComponent from 'components/LoaderComponent';
import DiagnosisTable from 'components/treatments/segments/DiagnosisTable';
import React, { Component } from 'react';
import { Field } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Icon } from 'semantic-ui-react';
import { InputOnChangeData } from 'semantic-ui-react/dist/commonjs/elements/Input/Input';
import Accordion from 'semantic-ui-react/dist/commonjs/modules/Accordion';
import { searchDiagnoses } from 'services/diagnosisServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import { DiagnosisDto, DiagnosisSearchRequest } from 'ts-types/api.types';

const StyledBorderGrid = styled(Grid)`
  border-bottom: 1px solid rgba(34, 36, 38, .1);
`;

interface Props extends MessageSourceProps {
  diagnosis: string,
  addDiagnosis: (diagnose: string) => void,
  activeIndex?: number,
  readOnly: boolean
}

interface State {
  foundDiagnoses: Array<DiagnosisDto>;
  selectedDiagnosis: string;
  searchBy: string;
  searchQuery: string;
  diagnosesLoaded: boolean;
  cancelTokenSource: CancelTokenSource;
  activeIndex?: number;
  initialDataLoaded: boolean;
}

class DiagnosisSegment extends Component<Props, State> {

  private mounted: boolean = false;

  tableRef = React.createRef();

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();
    const {activeIndex} = this.props;

    this.state = {
      foundDiagnoses: [],
      selectedDiagnosis: "",
      diagnosesLoaded: true,
      searchBy: "code",
      searchQuery: "",
      cancelTokenSource,
      activeIndex: activeIndex,
      initialDataLoaded: false
    };
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  onSearchByCriteriaChange = (event: any, criteria: string) => {

    event.preventDefault();

    this.setState({
      searchBy: criteria
    }, this.searchDiagnoses);
  };

  onSearchQueryChange = (
      event: React.ChangeEvent<HTMLInputElement>,
      data: InputOnChangeData) => {

    this.setState({
          searchQuery: data.value
        },
        this.searchDiagnosesDelayed);
  };

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;

    cancelTokenSource.cancel('Operation canceled by user');
  };

  searchDiagnoses = async () => {

    if (!this.mounted) {
      return;
    }

    const {
      searchBy,
      searchQuery,
      cancelTokenSource
    } = this.state;

    this.setState({
      diagnosesLoaded: false
    });

    const onFinally = () => {
      this.setState({
        diagnosesLoaded: true
      })
    };

    let searchRequest: Partial<DiagnosisSearchRequest> = {
      code: undefined,
      description: undefined
    }

    if (searchBy === "code") {
      searchRequest.code = searchQuery || "";
    } else {
      searchRequest.description = searchQuery || "";
    }

    try {
      const response = await searchDiagnoses(searchRequest, cancelTokenSource);
      await this.setState({
        foundDiagnoses: response
      });
    } finally {
      onFinally();
    }
  };

  searchDiagnosesDelayed = debounce(this.searchDiagnoses, 300);

  toggleAccordion = (e: any, titleProps: any) => {

    const {
      activeIndex,
      initialDataLoaded
    } = this.state;

    const {index} = titleProps;
    const newIndex = activeIndex === index ? -1 : index;

    this.setState({activeIndex: newIndex});

    if (newIndex >= 0 && !initialDataLoaded) {
      this.searchDiagnoses().then(() => {
        window.requestAnimationFrame(() => {
          this.tableRef.current && (this.tableRef.current as any).scrollIntoView();
        });
      });
      this.setState({
        initialDataLoaded: true
      });
    }
  };

  render(): React.ReactNode {

    const {
      readOnly,
      getMessage
    } = this.props;

    const {
      diagnosesLoaded,
      activeIndex
    } = this.state;

    return (
        <StyledBorderGrid>
          <Accordion fluid>
            <Accordion.Title active={activeIndex === 0} index={0} onClick={this.toggleAccordion}>
              <h4><Icon name="dropdown"/>{getMessage('treatment.diagnosis')}</h4>
            </Accordion.Title>
            <Accordion.Content active={activeIndex === 0} style={{paddingBottom: 25}}>
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column>
                    {this.renderDiagnosisSearchForm()}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2}>
                    {getMessage("diagnosis")}:
                  </Grid.Column>
                  <Grid.Column width={14}>
                    <Field
                        name="diagnosis"
                        component={Input}
                        disabled={readOnly}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    {!diagnosesLoaded
                        ? <LoaderComponent message={getMessage("diagnosis.loading")}/>
                        : <DiagnosisTable
                            foundDiagnoses={this.state.foundDiagnoses}
                            searchBy={this.state.searchBy}
                            readOnly={this.props.readOnly}
                            onDiagnosisClicked={this.props.addDiagnosis}
                            childRef={this.tableRef}
                        />}
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </Accordion.Content>
          </Accordion>
        </StyledBorderGrid>
    );
  }

  renderDiagnosisSearchForm = (): React.ReactNode => {

    const {
      readOnly,
      getMessage
    } = this.props;

    const {searchBy} = this.state;


    return (
        <Grid columns={2}>
          <Grid.Row>
            <Grid.Column width={2} verticalAlign="middle">
              {getMessage(`${searchBy}`)}:
            </Grid.Column>
            <Grid.Column width={searchBy === "code" ? 5 : 9}>
              <StyledInput
                  fluid
                  name="searchBy"
                  className="input-field"
                  disabled={readOnly}
                  onChange={this.onSearchQueryChange}
              />
            </Grid.Column>
            <Grid.Column width={searchBy === "code" ? 9 : 5} textAlign="right">
              <SearchByButtonsStyle size="tiny">
                <Button primary={searchBy === 'code' && true}
                        compact
                        basic={searchBy === 'code' ? undefined : true}
                        onClick={(event) =>
                            this.onSearchByCriteriaChange(event, "code")}
                >
                  {getMessage("code")}
                </Button>
                <Button primary={searchBy === 'description' && true}
                        compact
                        basic={searchBy === 'description' ? undefined : true}
                        onClick={(event) =>
                            this.onSearchByCriteriaChange(event, "description")}
                >
                  {getMessage("description")}
                </Button>
              </SearchByButtonsStyle>
            </Grid.Column>
          </Grid.Row>
        </Grid>
    );
  };

}

export default withMessages(DiagnosisSegment);
